import { useMemo, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { Autocomplete } from '@repo/videa-components/src/index';
import { Switch } from '@repo/videa-components/src/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import type { FieldErrors } from 'react-hook-form';

import type { LocationSearchPractice } from '@/react-query/useReferralInfo';

import type { ReferralFormValues } from '../../types';
import {
  transformSpecialtiesToOptions,
  transformPracticesToOptions,
  transformSpecialistsToOptions,
} from './locationSearchUtils';

interface SearchResultsSummaryProps {
  postalCode: string;
  debouncedPostalCode: string;
  isSearchLoading: boolean;
  locationSearchResults?: {
    total_practices: number;
    total_specialists: number;
  };
  selectedSpecialtyId?: number;
  practicesCount: number;
}

const SearchResultsSummary = ({
  postalCode,
  debouncedPostalCode,
  isSearchLoading,
  locationSearchResults,
  selectedSpecialtyId,
  practicesCount,
}: SearchResultsSummaryProps) => {
  const { t } = useLingui();

  // Show prompt when no postal code is entered
  if (!postalCode || postalCode.length < 5) {
    return (
      <div className="vai-p-4 vai-bg-blue-50 vai-border vai-border-blue-200 vai-rounded-md">
        <p className="vai-text-sm vai-text-blue-800">
          {t`Enter a postal code and select a radius to search for specialist practices near you.`}
        </p>
      </div>
    );
  }

  // Show loading state
  if (isSearchLoading && debouncedPostalCode.length >= 5) {
    return (
      <div className="vai-p-4 vai-bg-gray-50 vai-border vai-border-gray-200 vai-rounded-md">
        <p className="vai-text-sm vai-text-gray-600">{t`Searching for practices...`}</p>
      </div>
    );
  }

  // Show search results summary
  if (debouncedPostalCode.length >= 5 && !isSearchLoading && locationSearchResults) {
    if (locationSearchResults.total_practices > 0) {
      return (
        <div className="vai-text-sm vai-text-gray-600">
          <div className="vai-space-y-1">
            <span>
              {t`Found`} {locationSearchResults.total_practices} {t`practice(s) with`}{' '}
              {locationSearchResults.total_specialists} {t`specialist(s) in the area`}
            </span>
            {selectedSpecialtyId && (
              <div className="vai-text-xs vai-text-gray-500">
                {practicesCount} {t`practice(s) match the selected specialty`}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="vai-p-4 vai-bg-yellow-50 vai-border vai-border-yellow-200 vai-rounded-md">
        <p className="vai-text-sm vai-text-yellow-800">
          {t`No practices found. Try increasing the search radius.`}
        </p>
      </div>
    );
  }

  return null;
};

interface SpecialistSelectionFormProps {
  // Data
  specialties: Array<{ id: number; name: string }>;
  practices: LocationSearchPractice[];
  specialists: Array<{
    id: number;
    specialistName: string;
    specialtyName: string;
  }>;

  // Form values
  selectedSpecialtyId?: number;
  selectedPracticeId?: number;
  selectedSpecialistId?: number;
  useNextAvailableSpecialist?: boolean;

  // Handlers
  onSpecialtyChange: (specialtyId: string) => void;
  onPracticeChange: (practiceId: string) => void;
  onSpecialistChange: (specialistId: string) => void;
  onNextAvailableToggle: (checked: boolean) => void;

  // Search state
  postalCode: string;
  debouncedPostalCode: string;

  // Loading states
  isSpecialtiesLoading: boolean;
  isPracticesLoading: boolean;
  isSearchLoading: boolean;

  // Search results
  locationSearchResults?: {
    total_practices: number;
    total_specialists: number;
  };

  // Form errors
  errors: FieldErrors<ReferralFormValues>;
}

export const SpecialistSelectionForm = ({
  specialties,
  practices,
  specialists,
  selectedSpecialtyId,
  selectedPracticeId,
  selectedSpecialistId,
  useNextAvailableSpecialist,
  onSpecialtyChange,
  onPracticeChange,
  onSpecialistChange,
  onNextAvailableToggle,
  postalCode,
  debouncedPostalCode,
  isSpecialtiesLoading,
  isPracticesLoading,
  isSearchLoading,
  locationSearchResults,
  errors,
}: SpecialistSelectionFormProps) => {
  const { t } = useLingui();

  // Local search states for autocomplete filtering
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [specialistSearch, setSpecialistSearch] = useState('');

  // Transform data for Autocomplete components with search filtering
  const specialtyOptions = useMemo(() => {
    return transformSpecialtiesToOptions(specialties, specialtySearch);
  }, [specialties, specialtySearch]);

  const practiceOptions = useMemo(() => {
    return transformPracticesToOptions(practices, practiceSearch);
  }, [practices, practiceSearch]);

  const specialistOptions = useMemo(() => {
    return transformSpecialistsToOptions(specialists, specialistSearch);
  }, [specialists, specialistSearch]);

  return (
    <div className="vai-flex-1 vai-space-y-6">
      <SearchResultsSummary
        postalCode={postalCode}
        debouncedPostalCode={debouncedPostalCode}
        isSearchLoading={isSearchLoading}
        locationSearchResults={locationSearchResults}
        selectedSpecialtyId={selectedSpecialtyId}
        practicesCount={practices.length}
      />

      {/* Specialty Field */}
      <div className="vai-space-y-2">
        <label className="vai-text-sm vai-font-medium">
          {t`Specialty`}
          <span className="vai-text-red-500">*</span>
        </label>
        <Autocomplete
          options={specialtyOptions}
          value={selectedSpecialtyId?.toString() || ''}
          onChange={onSpecialtyChange}
          search={specialtySearch}
          onInputChange={setSpecialtySearch}
          disabled={!debouncedPostalCode || debouncedPostalCode.length < 5}
          placeholder={
            !debouncedPostalCode || debouncedPostalCode.length < 5
              ? t`Enter postal code first`
              : isSpecialtiesLoading
                ? t`Loading specialties...`
                : specialties.length === 0
                  ? t`No specialties found in area`
                  : t`Search specialty...`
          }
          noOptionsText={t`No specialties found`}
        />
        {errors.specialtyId && (
          <p className="vai-text-red-500 vai-text-sm">
            {errors.specialtyId.message}
          </p>
        )}
      </div>

      {/* Practice Field */}
      <div className="vai-space-y-2">
        <label className="vai-text-sm vai-font-medium">
          {t`Practice`}
          <span className="vai-text-red-500">*</span>
        </label>
        <Autocomplete
          options={practiceOptions}
          value={selectedPracticeId?.toString() || ''}
          onChange={onPracticeChange}
          search={practiceSearch}
          onInputChange={setPracticeSearch}
          disabled={
            !debouncedPostalCode ||
            debouncedPostalCode.length < 5 ||
            !selectedSpecialtyId
          }
          placeholder={
            !debouncedPostalCode || debouncedPostalCode.length < 5
              ? t`Enter postal code first`
              : !selectedSpecialtyId
                ? t`Select specialty first`
                : isPracticesLoading
                  ? t`Loading practices...`
                  : practices.length === 0
                    ? t`No practices found for selected specialty`
                    : t`Search practice...`
          }
          noOptionsText={t`No practices found`}
        />
        {errors.referralPracticeId && (
          <p className="vai-text-red-500 vai-text-sm">
            {errors.referralPracticeId.message}
          </p>
        )}
      </div>

      {/* Specialist Field */}
      <div className="vai-space-y-2">
        <div className="vai-flex vai-items-center vai-justify-between">
          <label className="vai-text-sm vai-font-medium">
            {t`Specialist`}
            <span className="vai-text-red-500">*</span>
          </label>

          {/* Next Available Specialist Toggle with Tooltip */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="vai-flex vai-items-center vai-space-x-2">
                  <Switch
                    checked={useNextAvailableSpecialist || false}
                    onCheckedChange={onNextAvailableToggle}
                    disabled={!selectedPracticeId}
                  />
                  <span className="vai-text-sm vai-text-gray-700">
                    {t`Next Available`}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="vai-max-w-xs">
                  {t`Faster scheduling - the specialist will assign the next available clinician at chosen practice`}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Specialist Selection - Conditional based on toggle */}
        {useNextAvailableSpecialist ? (
          <div className="vai-p-3 vai-bg-green-50 vai-border vai-border-green-200 vai-rounded-md">
            <p className="vai-text-sm vai-text-green-800">
              {t`The specialist clinic will schedule your patient with the next available provider.`}
            </p>
          </div>
        ) : (
          <div className="vai-space-y-1">
            <Autocomplete
              options={specialistOptions}
              value={selectedSpecialistId?.toString() || ''}
              onChange={onSpecialistChange}
              search={specialistSearch}
              onInputChange={setSpecialistSearch}
              disabled={!selectedPracticeId}
              placeholder={
                !selectedPracticeId
                  ? t`Select practice first`
                  : specialists.length === 0
                    ? t`No specialists found`
                    : t`Search specialist...`
              }
              noOptionsText={t`No specialists found`}
            />
            {errors.referralSpecialistId && (
              <p className="vai-text-red-500 vai-text-sm">
                {errors.referralSpecialistId.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

